﻿import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { User, ArrowLeft, StickyNote, Plus, Trash2, Phone, Mail } from "lucide-react";
import { MaintenanceSectionHeader } from "@/components/layout/maintenance";
import { Button } from "@/components/shadcn/button";
import { DeleteConfirmDialog } from "@/components/common/Dialog";
import { Spinner } from "@/components/common/Loading/Spinner";
import { LoadingPage } from "@/components/common/Loading/LoadingPage";
import { fetchContactDetails, fetchContactNotes, createNote, updateNote, deleteNote, patchContact, patchContactUd, deleteContact } from "@/api/contact/api";
import { validateEmail } from "@/utils/fieldValidation";
import { useLayout } from "@/contexts/LayoutContext";
import { showErrorToast, showSuccessToast } from "@/hooks/use-toast";
import { useAvatarColor, useReturnTo } from "@/hooks";
import { useRoles } from "@/contexts/RolesContext";
import { UserRole } from "@/types/domain";
import type { ContactDetails, ContactNote } from "@/types/domain";
import { InlineEditField } from "@/components/common/InlineEditField";
import { ContactNotesList } from "./ContactNotesList";
import { ContactAddressPanel } from "./ContactAddressPanel";
import { ContactUdFields } from "./ContactUdFields";
import { SectionHeading } from "./SectionHeading";
import { NoteDialog } from "@/components/common/NoteDialog";
import { AddressEditDialog } from "@/components/common/AddressEditDialog";

const CONTACT_STATUS_LABEL: Record<string, string> = { Active: "Active", Inactive: "Inactive", InfoOnly: "Info Only" };
const CONTACT_STATUS_OPTIONS = [
  { value: "Active",   label: "Active" },
  { value: "Inactive", label: "Inactive" },
  { value: "InfoOnly", label: "Info Only" },
];
const statusLabel = (s: string) => CONTACT_STATUS_LABEL[s] ?? s;

export const ContactDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { goBack } = useReturnTo();
  const { data } = useLayout();
  const { getAvatarColor, getInitials } = useAvatarColor();
  const { hasRole } = useRoles();
  const canDelete = hasRole(UserRole.DeleteContact);
  const [details, setDetails] = useState<ContactDetails | null>(null);
  const [notes, setNotes] = useState<{ items: ContactNote[]; totalCount: number } | null>(null);
  const [notesPage, setNotesPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeEditKey, setActiveEditKey] = useState<string | null>(null);

  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<ContactNote | null>(null);
  const [addressEditOpen, setAddressEditOpen] = useState(false);
  const [deleteContactOpen, setDeleteContactOpen] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);

  const handleDeleteContact = async () => {
    if (!id) return;
    setIsDeletingContact(true);
    try {
      await deleteContact(id);
      showSuccessToast("Contact deleted.");
      goBack();
    } catch {
      showErrorToast("Failed to delete contact.");
      setIsDeletingContact(false);
    }
  };

  // Factory: patch a single scalar field and update local state
  const patchField = (key: keyof ContactDetails) => async (value: string) => {
    if (!id) return;
    await patchContact(id, { [key]: value });
    setDetails((prev) => prev ? { ...prev, [key]: value } : prev);
  };

  const handleAddressSave = async (fields: {
    address1: string; address2: string; address3: string; address4: string;
    town: string; county: string; postcode: string; country: string;
  }) => {
    if (!id) return;
    await patchContact(id, fields);
    setDetails((prev) => prev ? {
      ...prev,
      contactOwnAddress1: fields.address1, contactOwnAddress2: fields.address2,
      contactOwnAddress3: fields.address3, contactOwnAddress4: fields.address4,
      contactOwnTown: fields.town, contactOwnCounty: fields.county,
      contactOwnPostcode: fields.postcode, contactOwnCountry: fields.country,
      // Also update resolved address fields
      address1: fields.address1, address2: fields.address2,
      address3: fields.address3, address4: fields.address4,
      town: fields.town, county: fields.county,
      postcode: fields.postcode, country: fields.country,
    } : prev);
  };

  const patchUd = (groupIdx: number, itemIdx: number, itemId: number) => async (value: string) => {
    if (!id) return;
    const item = details?.userDefinedGroups[groupIdx]?.items[itemIdx];
    if (item?.isReadOnly) return;
    await patchContactUd(id, itemId, value);
    setDetails((prev) => {
      if (!prev) return prev;
      const userDefinedGroups = prev.userDefinedGroups.map((g, gi) =>
        gi !== groupIdx ? g : {
          ...g,
          items: g.items.map((item, ii) => ii !== itemIdx ? item : { ...item, value }),
        }
      );
      return { ...prev, userDefinedGroups };
    });
  };

  const openAddNote = () => {
    setEditingNote(null);
    setNoteDialogOpen(true);
  };

  const openEditNote = (note: ContactNote) => {
    setEditingNote(note);
    setNoteDialogOpen(true);
  };

  const refreshNotes = async (page = notesPage) => {
    if (!id || !details) return;
    const fetched = await fetchContactNotes(id, details.companyId, page);
    setNotes(fetched);
  };

  const handleSaveNote = async (subject: string, body: string) => {
    if (!id || !details) return;
    if (editingNote) {
      await updateNote(editingNote.id, { subject, body });
    } else {
      await createNote({ contactId: id, companyId: details.companyId, subject, body });
    }
    setNotesPage(1);
    await refreshNotes(1);
    showSuccessToast(editingNote ? "Note updated." : "Note added.");
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await deleteNote(noteId);
      setNotesPage(1);
      await refreshNotes(1);
      showSuccessToast("Note deleted.");
    } catch {
      showErrorToast("Failed to delete note.");
    } finally {
      setDeleteNoteId(null);
    }
  };

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    setError(null);
    fetchContactDetails(id)
      .then((d) => {
        setDetails(d);
        setNotes(null);
        fetchContactNotes(id, d.companyId, 1)
          .then(setNotes)
          .catch(() => setNotes({ items: [], totalCount: 0 }));
      })
      .catch(() => setError("Failed to load contact details."))
      .finally(() => setIsLoading(false));
  }, [id]);

  const contactOwnAddressLines = details
    ? [details.contactOwnAddress1, details.contactOwnAddress2, details.contactOwnAddress3, details.contactOwnAddress4,
       details.contactOwnTown, details.contactOwnCounty, details.contactOwnPostcode, details.contactOwnCountry].filter(Boolean)
    : [];

  const companyAddressLines = details
    ? [details.companyAddress1, details.companyAddress2, details.companyAddress3, details.companyAddress4,
       details.companyTown, details.companyCounty, details.companyPostcode, details.companyCountry].filter(Boolean)
    : [];

  // Derived from individual fields so header updates when title/firstName/lastName are patched
  const displayName = details
    ? [details.title, details.firstName, details.lastName].filter(Boolean).join(" ")
    : "";

  const companyUrl = details ? `${data.appRoutes.dashboard}?customerid=${details.companyId}` : null;
  // Map uses contact's own address if set, falls back to company address
  const mapAddressLines = contactOwnAddressLines.length > 0 ? contactOwnAddressLines : companyAddressLines;

  // Shared props for all inline-editable fields to enforce single-active-edit
  const ep = (key: string) => ({ editKey: key, activeEditKey, onActivate: setActiveEditKey });

  return (
    <div className="w-full font-sans bg-white">
      <div className="bg-white mx-2 my-0 py-1.25 px-3.75">
        <MaintenanceSectionHeader title="Contact">
          {canDelete && details && (
            <Button variant="outline" size="sm" className="border-red-400 text-red-600 hover:bg-red-50 font-semibold" onClick={() => setDeleteContactOpen(true)}>
              <Trash2 size={14} />
              Delete
            </Button>
          )}
          <Button variant="outline" size="sm" className="border-kPurple-500 text-kPurple-500 hover:bg-kPurple-50 font-semibold" onClick={goBack}>
            <ArrowLeft size={14} />
            Back
          </Button>
        </MaintenanceSectionHeader>

        {isLoading && <LoadingPage message="Loading contact…" />}

        {error && <p className="text-sm text-destructive py-4">{error}</p>}

        {details && !isLoading && (
          <div className="space-y-8 pb-8">

            {/* Profile header + fields + map — mirrors cd-profile-layout */}
            <div>
              <div className="px-4 pt-3 pb-6 border-b border-gray-200 flex items-center gap-4">
                <div className="flex items-center justify-center w-14 h-14 rounded-full text-white text-lg font-semibold shrink-0"
                  style={{ backgroundColor: getAvatarColor(details.contactId) }}>
                  {getInitials(details.firstName, details.lastName) || <User size={20} />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-semibold text-gray-800 truncate">{displayName}</h2>
                    {details.isPrimary && (
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-[#e8f4fd] text-[#0275d8] border border-[#b8d9f5] whitespace-nowrap">
                        Primary
                      </span>
                    )}
                    {details.status !== "Active" && (
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-[#fde8e8] text-[#c0392b] border border-[#f5b8b8] whitespace-nowrap">
                        {statusLabel(details.status)}
                      </span>
                    )}
                  </div>
                  {details.jobDescription && (
                    <p className="text-sm text-muted-foreground truncate">{details.jobDescription}</p>
                  )}
                  {companyUrl && (
                    <a href={companyUrl} className="text-sm text-blue-600 hover:underline truncate block">
                      {details.accountNo}{" - "}{details.companyName}
                    </a>
                  )}
                </div>
              </div>

              {/* cd-profile-layout: fields left, address+map right */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px]">
                {/* Main fields — 3-col shrinking grid */}
                <div className="px-4 py-0 border-r border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-7">
                    <InlineEditField {...ep("title")} label="Title" value={details.title} onSave={patchField("title")} maxLength={50} isReadOnly={details.fieldOptions.contactName.isReadOnly} />
                    <InlineEditField {...ep("firstName")} label="First Name" value={details.firstName} onSave={patchField("firstName")} maxLength={50} isMandatory={details.fieldOptions.contactName.isMandatory} isReadOnly={details.fieldOptions.contactName.isReadOnly} />
                    <InlineEditField {...ep("lastName")} label="Last Name" value={details.lastName} onSave={patchField("lastName")} maxLength={50} isMandatory={details.fieldOptions.contactName.isMandatory} isReadOnly={details.fieldOptions.contactName.isReadOnly} />
                    <InlineEditField {...ep("salutation")} label="Salutation" value={details.salutation} onSave={patchField("salutation")} maxLength={50} />
                    <InlineEditField {...ep("preferredName")} label="Preferred Name" value={details.preferredName} onSave={patchField("preferredName")} maxLength={50} />
                    <InlineEditField {...ep("jobDescription")} label="Job Description" value={details.jobDescription} onSave={patchField("jobDescription")} maxLength={50} isMandatory={details.fieldOptions.jobDescription.isMandatory} isReadOnly={details.fieldOptions.jobDescription.isReadOnly} />
                    <InlineEditField {...ep("department")} label="Department" value={details.department} onSave={patchField("department")} maxLength={50} isMandatory={details.fieldOptions.department.isMandatory} isReadOnly={details.fieldOptions.department.isReadOnly} />

                    <InlineEditField {...ep("email")}
                      label="Email" value={details.email} onSave={patchField("email")} inputType="email" maxLength={255}
                      actionHref={`mailto:${details.email}`} actionIcon={<Mail size={12} />}
                      isMandatory={details.fieldOptions.email.isMandatory} isReadOnly={details.fieldOptions.email.isReadOnly}
                      validate={validateEmail}
                    />
                    <InlineEditField {...ep("workTelephone")}
                      label="Office" value={details.workTelephone} onSave={patchField("workTelephone")} inputType="tel" maxLength={30}
                      actionHref={`tel:${details.workTelephone || details.companyTelephone}`} actionIcon={<Phone size={12} />}
                      showActionWhenEmpty={!!details.companyTelephone}
                      displayRender={(v) => v || (details.companyTelephone
                        ? <span className="text-gray-400">{details.companyTelephone} <span className="text-[11px]">(company)</span></span>
                        : <span className="text-gray-300 text-[13px]">—</span>)}
                      isMandatory={details.fieldOptions.telephoneOffice.isMandatory} isReadOnly={details.fieldOptions.telephoneOffice.isReadOnly}
                    />
                    <InlineEditField {...ep("mobileTelephone")}
                      label="Mobile" value={details.mobileTelephone} onSave={patchField("mobileTelephone")} inputType="tel" maxLength={30}
                      actionHref={`tel:${details.mobileTelephone}`} actionIcon={<Phone size={12} />}
                      isMandatory={details.fieldOptions.telephoneMobile.isMandatory} isReadOnly={details.fieldOptions.telephoneMobile.isReadOnly}
                    />
                    <InlineEditField {...ep("homeTelephone")}
                      label="Home" value={details.homeTelephone} onSave={patchField("homeTelephone")} inputType="tel" maxLength={30}
                      actionHref={`tel:${details.homeTelephone}`} actionIcon={<Phone size={12} />}
                      isMandatory={details.fieldOptions.telephoneHome.isMandatory} isReadOnly={details.fieldOptions.telephoneHome.isReadOnly}
                    />
                    <InlineEditField {...ep("fax")} label="Fax" value={details.fax} onSave={patchField("fax")} inputType="tel" maxLength={30} isMandatory={details.fieldOptions.fax.isMandatory} isReadOnly={details.fieldOptions.fax.isReadOnly} />

                    <InlineEditField {...ep("status")}
                      label="Status"
                      value={details.status}
                      options={CONTACT_STATUS_OPTIONS}
                      onSave={async (v) => {
                        await patchContact(id!, { status: v });
                        setDetails((prev) => prev ? { ...prev, status: v } : prev);
                      }}
                      displayRender={(v) => v === "Active"
                        ? <span className="text-[15px] text-gray-800">Active</span>
                        : (
                        <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-[#fde8e8] text-[#c0392b] border border-[#f5b8b8] whitespace-nowrap">
                          {v}
                        </span>
                      )}
                    />

                    {/* Primary Contact uses a checkbox toggle rather than text input */}
                    <div className="flex flex-col py-4.5 pb-2 border-b border-gray-100 min-w-0">
                      <span className="text-[11px] font-normal text-gray-400 uppercase tracking-wider mb-1">Primary Contact</span>
                      <div className="flex items-center gap-2 min-h-6 mt-0.5">
                        <input
                          type="checkbox"
                          checked={details.isPrimary}
                          className="w-4 h-4 cursor-pointer"
                          onChange={async (e) => {
                            const newValue = e.target.checked;
                            await patchContact(id!, { isPrimary: newValue });
                            setDetails((prev) => prev ? { ...prev, isPrimary: newValue } : prev);
                          }}
                        />
                        {details.isPrimary && (
                          <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-[#e8f4fd] text-[#0275d8] border border-[#b8d9f5] whitespace-nowrap">
                            Primary
                          </span>
                        )}
                      </div>
                    </div>

                    <InlineEditField {...ep("comments")} label="Comments" value={details.comments} onSave={patchField("comments")} multiline fullWidth maxLength={8000} isMandatory={details.fieldOptions.comments.isMandatory} isReadOnly={details.fieldOptions.comments.isReadOnly} />

                    <div className="col-span-full py-2 pt-3 text-xs text-muted-foreground space-y-0.5">
                      <div>Created {details.createdDate}{details.createdByName ? ` by ${details.createdByName}` : ""}</div>
                      <div>Modified {details.modifiedDate}{details.modifiedByName ? ` by ${details.modifiedByName}` : ""}</div>
                    </div>
                  </div>
                </div>

                <ContactAddressPanel
                  contactAddressLines={contactOwnAddressLines}
                  companyAddressLines={companyAddressLines}
                  mapAddressLines={mapAddressLines}
                  apiKey={data.googleMapsApiKey}
                  isMandatory={details.fieldOptions.address.isMandatory}
                  isReadOnly={details.fieldOptions.address.isReadOnly}
                  onEditClick={() => { setActiveEditKey(null); setAddressEditOpen(true); }}
                />
              </div>
            </div>

            <ContactUdFields
              groups={details.userDefinedGroups}
              patchUd={patchUd}
              activeEditKey={activeEditKey}
              onActivateEdit={setActiveEditKey}
            />

            {/* Notes */}
            <div className="px-4">
              <SectionHeading
                icon={<StickyNote size={15} />}
                action={
                  <Button variant="primary" size="sm" className='bg-kPurple-500 hover:bg-kPurple-700 text-white hover:text-white font-bold' onClick={openAddNote}>
                    <Plus size={13} />
                    Add Note
                  </Button>
                }
              >
                Notes
              </SectionHeading>
              {notes === null ? (
                <div className="py-2"><Spinner size="sm" /></div>
              ) : notes.totalCount > 0 ? (
                <ContactNotesList
                  notes={notes.items}
                  totalCount={notes.totalCount}
                  page={notesPage}
                  onPageChange={(p) => { setNotesPage(p); refreshNotes(p); }}
                  onEdit={openEditNote}
                  onDelete={setDeleteNoteId}
                />
              ) : (
                <p className="text-sm text-muted-foreground py-2">No notes for this contact.</p>
              )}
            </div>

          </div>
        )}
      </div>

      {/* Note add/edit dialog */}
      <NoteDialog
        open={noteDialogOpen}
        onOpenChange={setNoteDialogOpen}
        note={editingNote}
        onSave={handleSaveNote}
      />
      {/* Address edit dialog */}
      {details && (
        <AddressEditDialog
          open={addressEditOpen}
          onOpenChange={setAddressEditOpen}
          initial={{
            address1: details.contactOwnAddress1, address2: details.contactOwnAddress2,
            address3: details.contactOwnAddress3, address4: details.contactOwnAddress4,
            town: details.contactOwnTown, county: details.contactOwnCounty,
            postcode: details.contactOwnPostcode, country: details.contactOwnCountry,
          }}
          companyAddress={{
            address1: details.companyAddress1, address2: details.companyAddress2,
            address3: details.companyAddress3, address4: details.companyAddress4,
            town: details.companyTown, county: details.companyCounty,
            postcode: details.companyPostcode, country: details.companyCountry,
          }}
          onSave={handleAddressSave}
          isMandatory={details.fieldOptions.address.isMandatory}
          isReadOnly={details.fieldOptions.address.isReadOnly}
        />
      )}

      <DeleteConfirmDialog
        open={deleteContactOpen}
        onOpenChange={setDeleteContactOpen}
        onConfirm={handleDeleteContact}
        title="Delete contact?"
        description={`This will permanently delete ${details?.contactName}. This cannot be undone.`}
        isDeleting={isDeletingContact}
      />

      <DeleteConfirmDialog
        open={deleteNoteId !== null}
        onOpenChange={(open) => { if (!open) setDeleteNoteId(null); }}
        onConfirm={() => deleteNoteId && handleDeleteNote(deleteNoteId)}
        title="Delete note?"
      />
    </div>
  );
};
